<h1>Dwolla Reconciliation</h1>
<nav mat-tab-nav-bar [tabPanel]="tabPanel">
  @for (tab of tabs | visibleFinanceTabs; track tab.id) {
    <a mat-tab-link [routerLink]="tab.link" [active]="tab.id === 'dwolla-reconciliation'">{{tab.title}}</a>
  }
</nav>
<mat-tab-nav-panel #tabPanel>
  @if (busy) { <mat-progress-bar mode="indeterminate"></mat-progress-bar> }
  @if (error) { <p role="alert" class="error">{{error}}</p> }
  @if (message) { <p role="status">{{message}}</p> }
  <section>
    <h2>Where the master Dwolla balance came from</h2>
    <p>Generate the interactive master-balance attribution report from current Dwolla history, Stripe payout and invoice evidence, and Wallit records. Monthly statements are optional. Balances without sufficient supporting evidence are marked “Not established.”</p>
    <p>Extract the downloaded ZIP and open funding-attribution.html. It works offline and includes supporting JSON, the debit/credit ledger, and monthly statement comparisons. Candidate payout matches remain clearly labeled as unconfirmed.</p>
    @if (data) { <p><strong>{{data.account.name}}</strong> · {{data.environment}} · Account {{data.account.id}}</p> }
    <mat-checkbox [(ngModel)]="allDates">All dates</mat-checkbox>
    @if (!allDates) {
      <div class="fields">
        <label>Start date <input type="date" [(ngModel)]="startDate"></label>
        <label>End date (inclusive) <input type="date" [(ngModel)]="endDate"></label>
      </div>
    }
    <label class="timezone">Date-range timezone
      <select [(ngModel)]="timezone"><option value="UTC">UTC</option><option value="America/New_York">America/New_York</option><option value="America/Los_Angeles">America/Los_Angeles</option></select>
    </label>
    <p>API history uses creation dates; statement postings use reconciliation dates. Monthly comparisons retain the full UTC statement month. All dates includes the history returned by Dwolla through the export cutoff.</p>
    <button mat-raised-button color="primary" [disabled]="busy || !data" (click)="generate()">Generate evidence ZIP</button>
  </section>
  <section>
    <h2>Original monthly statements (optional)</h2>
    @if (data) {
      @if (monthsNeedingUpload) {
        <p role="status" class="statement-warning"><strong>{{monthsNeedingUpload}} completed month(s) need statement uploads.</strong> Check Dwolla for the missing PDFs and CSVs below.</p>
      } @else { <p>Both files are uploaded for every month shown through {{completedMonth}}.</p> }
      <p>Months end at midnight UTC. This list shows uploaded months and gaps through the last completed month ({{completedMonth}}). Earlier history is not assessed until files are uploaded. File availability in Dwolla has not been checked. Uploaded files still require reconciliation in the report.</p>
      <div class="table-wrap"><table aria-label="Monthly statement uploads">
        <thead><tr><th>Month (UTC)</th><th>PDF</th><th>CSV</th><th>Upload status</th></tr></thead>
        <tbody>@for (month of statementMonths; track month.month) {
          <tr [class.statement-warning]="month.overdue"><td>{{month.month}}</td><td>{{month.pdf ? 'Uploaded' : 'Missing'}}</td><td>{{month.csv ? 'Uploaded' : 'Missing'}}</td><td>{{month.status}}</td></tr>
        }</tbody>
      </table></div>
    }
    <p>Upload original Dwolla PDFs and CSVs together. Keep names such as 2025-12-summary.pdf and 2025-12-data.csv. PDFs supply monthly totals; CSVs supply posting details. Identical files are deduplicated; conflicting originals are rejected.</p>
    <p>When a new month's files become available in Dwolla, upload them here and generate a new report. Each report refreshes the attribution research and uses the files available when requested; existing reports remain unchanged.</p>
    <input type="file" multiple accept=".pdf,.csv" [disabled]="busy || !data" (change)="chooseFiles($event)" aria-label="Choose original Dwolla statements">
    <p>Up to 160 files, 2 MB each, 50 MB total. {{files.length}} selected.</p>
    <mat-checkbox [(ngModel)]="confirmed" [disabled]="!files.length">These are unmodified Dwolla originals for the master account shown above.</mat-checkbox>
    <p><button mat-raised-button [disabled]="busy || !files.length || !confirmed" (click)="upload()">Upload statements</button></p>
    @if (data) {
      <details><summary>{{data.statements.length}} archived original files</summary>
        <div class="table-wrap"><table><thead><tr><th>File</th><th>Uploaded</th><th>Uploaded by</th></tr></thead><tbody>
          @for (file of data.statements; track file.id) { <tr><td>{{file.originalName}}</td><td>{{file.uploadedAt | date:'medium':'UTC'}} UTC</td><td>{{file.uploadedBy}}</td></tr> }
        </tbody></table></div>
      </details>
    }
  </section>
  <section>
    <h2>Generated reports</h2>
    <button mat-button [disabled]="busy" (click)="refresh()">Refresh</button>
    @if (data) {
      @for (run of data.runs; track run.id) {
        <article><strong>{{run.createdAt | date:'medium':'UTC'}} UTC — {{run.state}}</strong><p>{{run.stage}}</p>
          @if (run.result) { <p>{{run.result.status}} · {{run.result.apiRows}} API rows · {{run.result.postingRows}} statement postings · {{run.result.exceptions}} exceptions</p> }
          @if (run.state === 'complete') { <button mat-raised-button [disabled]="busy" (click)="download(run)">Download ZIP</button> }
          @if (run.state === 'complete' || run.state === 'failed') { <button mat-button [disabled]="busy" (click)="remove(run)">Remove from list</button> }
        </article>
      } @empty { <p>No reports generated yet.</p> }
    }
  </section>
</mat-tab-nav-panel>
